/* eslint-disable better-mutation/no-mutation */

import { createSlice } from '@reduxjs/toolkit';
import getInitialState from 'tradera/state/views/checkout/initial-state';

const checkoutSlice = createSlice({
    name: 'checkout',
    initialState: getInitialState(),
    reducers: {
        receiveCheckout: (state, { payload }) => {
            const {
                buyerMember,
                purchaseOrder,
                sellerMember,
                shippingInfo,
                combinedShippingInfo,
                ongoingPaymentAttempt,
                shippingFeeRule,
                verificationStatus,
            } = payload;
            state.buyerMember = { ...buyerMember };
            state.sellerMember = { ...sellerMember };
            state.isFetching = false;
            state.hasError = false;
            state.purchaseOrderId = purchaseOrder.purchaseOrderId;
            state.orderNumber = purchaseOrder.orderNumber;
            state.paidStatus = purchaseOrder.paidStatus;
            state.ongoingPaymentAttempt = ongoingPaymentAttempt;
            state.shippingInfo.shippingOptions = shippingInfo.shippingOptions;
            state.shippingInfo.shippingPriceRange =
                shippingInfo.shippingPriceRange;
            state.shippingInfo.selectedCollectionPoint =
                shippingInfo.collectionPointId !== undefined
                    ? { id: shippingInfo.collectionPointId }
                    : undefined;
            state.shippingInfo.isBooked = shippingInfo.isBooked;
            state.combinedShippingInfo = combinedShippingInfo;
            state.shippingFeeRule = shippingFeeRule;
            state.purchaseOrder = purchaseOrder;
            state.shippingOptionsLoading = false;
            state.verificationStatus = verificationStatus;
        },
        receiveShippingOptions: (state, { payload }) => {
            state.shippingInfo.shippingOptions = payload;
        },
        setShippingOptionsLoading: (state, { payload }) => {
            state.shippingOptionsLoading = payload.isLoading;
        },
        receivePaymentOptions: (state, { payload }) => {
            state.payment.paymentOptions = payload;
            state.payment.isFetching = false;
        },
        receiveCollectionPoints: (state, { payload }) => {
            state.shippingInfo.collectionPoints = payload;
        },
        setFetching: (state, { payload }) => {
            state.isFetching = payload;
            state.hasError = false;
        },
        openModal: (state, { payload }) => {
            const { open, paymentAttemptId, trustlyRedirectUrl } = payload;
            state.modal = {
                open,
                paymentAttemptId,
                trustlyRedirectUrl,
            };
        },
        cancelPayment: (state) => {
            state.checkoutSummary.hasError = false;
            state.checkoutSummary.isFetching = false;
        },
        closeModal: (state) => {
            state.modal = {
                open: '',
            };
        },
        setHasError: (state, { payload }) => {
            state.hasError = payload;
            state.isFetching = false;
        },
        setPaymentFetching: (state, { payload }) => {
            state.payment.isFetching = payload;
            state.payment.hasError = false;
        },
        setPaymentHasError: (state, { payload }) => {
            state.payment.hasError = payload;
            state.payment.isFetching = false;
        },
        setPaymentIsValid: (state, { payload }) => {
            state.payment.isValid = payload;
        },
        setPaymentMethodData: (state, { payload }) => {
            state.payment.paymentMethodData = payload;
        },
        setCheckoutSummaryFetching: (state, { payload }) => {
            state.checkoutSummary.isFetching = payload;
            state.checkoutSummary.hasError = false;
        },
        setCheckoutSummaryHasError: (state, { payload }) => {
            state.checkoutSummary.hasError = payload;
            state.checkoutSummary.isFetching = false;
        },
        setSelectedPayment: (state, { payload }) => {
            state.payment.selectedPayment = payload;
            state.checkoutSummary.hasError = false;
        },
        setOrderCalculation: (state, { payload }) => {
            state.payment.orderCalculation = payload;
        },
        setBuyerProtection: (state, { payload }) => {
            state.payment.buyerProtection = payload;
        },
        setUserSelectedBuyerProtection: (state, { payload }) => {
            state.payment.userSelectedBuyerProtection = payload;
        },
        setSelectedShipping: (state, { payload }) => {
            state.selectedShipping = payload;
        },
        setOngoingPaymentAttempt: (state, { payload }) => {
            state.ongoingPaymentAttempt = payload;
        },
        setCombinedShippingInfo: (state, { payload }) => {
            state.combinedShippingInfo = {
                ...state.combinedShippingInfo,
                ...payload,
            };
        },
        setBuyerMember: (state, { payload }) => {
            state.buyerMember = { ...state.buyerMember, ...payload };
        },
        setBuyerMemberAddress: (state, { payload }) => {
            state.buyerMember.address = {
                ...state.buyerMember.address,
                ...payload,
            };
        },
        setCollectionPoint: (state, { payload }) => {
            state.shippingInfo.selectedCollectionPoint = payload;
        },
        setShippingInfoFetching: (state, { payload }) => {
            state.shippingInfo.isFetching = payload;
        },
        setCollectionPointSaving: (state, { payload }) => {
            state.shippingInfo.savingCollectionPoint = payload;
        },
        setDropinLoading: (state, { payload }) => {
            state.payment.dropinLoading = payload;
        },
        setDiscountCodesValidating: (state, { payload }) => {
            state.payment.discountCodesValidating = payload;
        },
        setDiscountCodes: (state, { payload }) => {
            state.payment.discountCodes = payload;
        },
        setPaymentMethodAmount: (state, { payload }) => {
            state.payment.paymentMethodAmount = payload;
        },
        setPreselectedValues: (state, { payload }) => {
            state.preselectedValues = payload;
        },
        setShowSelectedPaymentAsPayButton: (state, { payload }) => {
            state.payment.showSelectedPaymentAsPayButton = payload;
        },
        setCurrencyCode: (state, { payload }) => {
            state.currencyCode = payload || state.currencyCode;
        },
    },
});

export const {
    openModal,
    closeModal,
    cancelPayment,
    receiveCheckout,
    receiveShippingOptions,
    setShippingOptionsLoading,
    receivePaymentOptions,
    receiveCollectionPoints,
    setFetching,
    setHasError,
    setCheckoutSummaryFetching,
    setCheckoutSummaryHasError,
    setPaymentFetching,
    setPaymentHasError,
    setPaymentIsValid,
    setPaymentMethodData,
    setSelectedPayment,
    setOrderCalculation,
    setSelectedShipping,
    setOngoingPaymentAttempt,
    setCombinedShippingInfo,
    setBuyerMember,
    setBuyerMemberAddress,
    setCollectionPoint,
    setShippingInfoFetching,
    setCollectionPointSaving,
    setBuyerProtection,
    setUserSelectedBuyerProtection,
    setDropinLoading,
    setDiscountCodesValidating,
    setDiscountCodes,
    setPaymentMethodAmount,
    setPreselectedValues,
    setShowSelectedPaymentAsPayButton,
    setCurrencyCode,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
